import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { ArrowLeft, TrendingUp, AlertTriangle, Lightbulb, Target, Heart, Zap, Brain, ChevronRight, Star, MessageSquare } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";

const MetricBar = ({ label, value, color }) => (
  <div data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex justify-between text-sm mb-1.5">
      <span className="text-white/60">{label}</span>
      <span className="font-semibold" style={{ color }}>{value}%</span>
    </div>
    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${value}%`, background: color }} />
    </div>
  </div>
);

const Section = ({ icon: Icon, title, children, delay = 0 }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-6 animate-fade-in" style={{ animationDelay: `${delay}s` }}>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl bg-[#002FA7]/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#002FA7]" />
      </div>
      <h3 className="font-['Outfit'] font-semibold text-lg">{title}</h3>
    </div>
    {children}
  </div>
);

const ConsultaDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dateLocaleMap = { de: "de-DE", it: "it-IT", en: "en-GB", es: "es-ES" };
  const dateLang = dateLocaleMap[lang] || "es-ES";

  useEffect(() => {
    const fetchConsultation = async () => {
      try {
        const res = await axios.get(`${API}/futura/consultation/${id}`);
        setConsultation(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "not_found" : "error");
      } finally {
        setLoading(false);
      }
    };
    fetchConsultation();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white">
        <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 py-20">
          <div className="space-y-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white/5 rounded-2xl p-6 animate-pulse">
                <div className="h-5 bg-white/10 rounded w-1/3 mb-4" />
                <div className="h-4 bg-white/10 rounded w-full mb-2" />
                <div className="h-4 bg-white/10 rounded w-2/3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/50 mb-4">{error === "not_found" ? t("detail_not_found") : t("detail_error")}</p>
          <Link to="/history" className="text-[#002FA7] hover:underline">{t("detail_back_history")}</Link>
        </div>
      </div>
    );
  }

  const resp = consultation.response || {};
  const qa = consultation.questions_and_answers || [];
  const isPremium = resp.plan_tier === "avanza";

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />

      <header className="relative z-20 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/history" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-testid="back-to-history">
            <ArrowLeft className="w-5 h-5" /><span className="font-medium">{t("detail_back_history")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="dark" />
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-8 pb-20 space-y-6">
        {/* Original question */}
        <div className="animate-fade-in">
          <p className="text-white/40 text-sm mb-2">
            {new Date(consultation.created_at).toLocaleDateString(dateLang, { day: "numeric", month: "long", year: "numeric" })}
          </p>
          <h1 className="font-['Outfit'] text-xl sm:text-2xl font-semibold tracking-tight leading-snug" data-testid="detail-question">
            {consultation.question}
          </h1>
        </div>

        {/* Q&A section */}
        {qa.length > 0 && (
          <Section icon={MessageSquare} title={t("detail_qa_title")} delay={0.1}>
            <div className="space-y-4">
              {qa.map((item, i) => (
                <div key={`qa-${i}-${item.q?.slice(0,10)}`} className="border-l-2 border-[#002FA7]/30 pl-4">
                  <p className="text-white/50 text-sm mb-1">{item.q}</p>
                  <p className="text-white/90 text-sm">{item.a}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Metrics */}
        <Section icon={TrendingUp} title={t("detail_metrics")} delay={0.15}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <MetricBar label={t("detail_success")} value={resp.success_probability || 0} color="#22c55e" />
            <MetricBar label={t("detail_recommendation")} value={resp.recommendation_level || 0} color="#002FA7" />
            <MetricBar label={t("detail_stress")} value={resp.stress_level || 0} color="#ef4444" />
            <MetricBar label={t("detail_happiness")} value={resp.happiness_level || 0} color="#f59e0b" />
          </div>
        </Section>

        {/* Recommendation */}
        <Section icon={Target} title={t("detail_recommendation_title")} delay={0.2}>
          <p className="text-white/90 font-medium mb-3" data-testid="detail-recommendation">{resp.recommendation}</p>
          {resp.recommendation_reason && (
            <p className="text-white/60 text-sm leading-relaxed">{resp.recommendation_reason}</p>
          )}
        </Section>

        {/* Motives */}
        {resp.motives?.length > 0 && (
          <Section icon={Lightbulb} title={t("detail_motives")} delay={0.25}>
            <ul className="space-y-2">
              {resp.motives.map((m, i) => (
                <li key={`motive-${i}`} className="flex items-start gap-3 text-sm text-white/80">
                  <ChevronRight className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Risks */}
        {resp.risks?.length > 0 && (
          <Section icon={AlertTriangle} title={t("detail_risks")} delay={0.3}>
            <ul className="space-y-2">
              {resp.risks.map((r, i) => (
                <li key={`risk-${i}`} className="flex items-start gap-3 text-sm text-white/80">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Alternative */}
        {resp.alternative && (
          <Section icon={Zap} title={t("detail_alternative")} delay={0.35}>
            <p className="text-white/80 text-sm">{resp.alternative}</p>
          </Section>
        )}

        {/* Premium: Top 3 Paths */}
        {isPremium && resp.top3_paths?.length > 0 && (
          <Section icon={Star} title={t("detail_top3")} delay={0.4}>
            <div className="space-y-4">
              {resp.top3_paths.map((p, i) => (
                <div key={`path-${i}-${p.path?.slice(0,10)}`} className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white/90">{p.path}</span>
                    <div className="flex gap-3 text-xs">
                      <span className="text-green-400">{t("detail_confidence")}: {p.confidence}%</span>
                      <span className="text-amber-400">{t("detail_risk")}: {p.risk}%</span>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm">{p.description}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Premium: Action Plan */}
        {isPremium && resp.action_plan?.length > 0 && (
          <Section icon={Brain} title={t("detail_action_plan")} delay={0.45}>
            <ol className="space-y-2">
              {resp.action_plan.map((step, i) => (
                <li key={`step-${i}`} className="flex items-start gap-3 text-sm text-white/80">
                  <span className="w-6 h-6 rounded-full bg-[#002FA7]/20 text-[#002FA7] flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {/* Premium Summary */}
        {isPremium && resp.premium_summary && (
          <div className="bg-gradient-to-r from-[#002FA7]/10 to-[#5B21B6]/10 border border-[#002FA7]/20 rounded-2xl p-6 animate-fade-in" style={{ animationDelay: "0.5s" }}>
            <div className="flex items-center gap-3 mb-3">
              <Heart className="w-5 h-5 text-[#002FA7]" />
              <h3 className="font-['Outfit'] font-semibold text-lg">{t("detail_premium_summary")}</h3>
            </div>
            <p className="text-white/80 text-sm leading-relaxed">{resp.premium_summary}</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ConsultaDetail;
